import { useEffect, useState } from 'react'
import usePlacesAutocomplete, { getDetails } from 'use-places-autocomplete'
import { useAttendeeFormContext } from 'components/Checkout/AttendeesForm/useAttendeeFormContext'
import {
  InputErrorLabel,
  InputLabel,
  InputLabelBlock,
  StyledInput
} from 'components/common/TextInput'
import useComponentVisible from 'components/common/useComponentVisible'
import { convertGeocoderAddress } from 'live/Import/ImportAttendeeConvertHelper'
import {
  ElevatedContainer,
  PostalAddressSuggestion,
  PostalAddressSuggestionsBox
} from './PostalAddressInput.style'

interface AddressSuggestion {
  place_id: string
  structured_formatting: { main_text: string; secondary_text: string }
}

/**
 * NEVER use this component alone, use `PostalAddressInput` instead.
 * It will manage the loading of google places API script.
 * @param param0
 * @returns
 */
export const LoadedPostalAddressInput = ({
  inputLabel,
  placeholder,
  disabled
}: {
  inputLabel?: string
  placeholder?: string
  disabled?: boolean
}) => {
  const {
    setValue: setHiddenFieldValue,
    register,
    watch,
    formState: { errors },
    clearErrors
  } = useAttendeeFormContext()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // if value changes from user input, then suggestions must be visible again
    setIsSuggestionsVisible(true)
    setValue(e.target.value)
    setPlaceId(undefined)
    setHiddenFieldValue('particulars.address', undefined)
  }

  const {
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions
  } = usePlacesAutocomplete({ debounce: 350 })

  const [placeId, setPlaceId] = useState<string>()

  const {
    ref,
    isComponentVisible: isSuggestionsVisible,
    setIsComponentVisible: setIsSuggestionsVisible
  } = useComponentVisible(false)

  const selectSuggestion = (obj: AddressSuggestion) => {
    setPlaceId(obj.place_id)

    setHiddenFieldValue(
      'particulars.address.postalAddressMain',
      obj.structured_formatting.main_text
    )
    setHiddenFieldValue(
      'particulars.address.postalAddressSecondary',
      obj.structured_formatting.secondary_text
    )
    // remove suggestions list
    clearSuggestions()
    setIsSuggestionsVisible(false)
  }

  const mainText = watch('particulars.address.postalAddressMain')
  const secondaryText = watch('particulars.address.postalAddressSecondary')

  useEffect(() => {
    if (mainText && secondaryText) {
      setValue(`${mainText}, ${secondaryText}`)
    } else {
      setValue('')
      setPlaceId(undefined)
      setHiddenFieldValue('particulars.address', undefined)
    }
  }, [mainText, secondaryText])

  useEffect(() => {
    if (!placeId || status !== 'OK') return

    const getAddressComponents = async () => {
      const details = await getDetails({
        placeId,
        fields: ['address_components', 'formatted_address']
      }) // as { address_components: { types: string[]; long_name: string }[] }
      if (typeof details === 'string') {
        throw new Error(`Address getDetails returned a string: ${details}`)
      }
      const { address_components = [] } = details
      const {
        postalAddressZipcode,
        postalAddressCity,
        postalAddressCountry,
        postalAddressAreaLevel1,
        postalAddressAreaLevel2
      } = convertGeocoderAddress(address_components)
      setHiddenFieldValue(
        'particulars.address.postalAddressZipcode',
        postalAddressZipcode
      )
      setHiddenFieldValue(
        'particulars.address.postalAddressCity',
        postalAddressCity
      )
      setHiddenFieldValue(
        'particulars.address.postalAddressCountry',
        postalAddressCountry
      )
      setHiddenFieldValue(
        'particulars.address.postalAddressAreaLevel1',
        postalAddressAreaLevel1
      )
      setHiddenFieldValue(
        'particulars.address.postalAddressAreaLevel2',
        postalAddressAreaLevel2
      )
      clearErrors('particulars.address.postalAddressMain')
    }
    getAddressComponents()
    setIsSuggestionsVisible(false)
    clearSuggestions()
  }, [placeId, status])

  const addressErrorMessage =
    errors.particulars?.address?.message ??
    errors.particulars?.address?.root?.message ??
    errors.particulars?.address?.postalAddressMain?.root?.message ??
    errors.particulars?.address?.postalAddressMain?.message
  return (
    <InputLabelBlock fullSize={true}>
      {inputLabel && <InputLabel>{inputLabel}</InputLabel>}
      <StyledInput
        placeholder={placeholder}
        value={value}
        error={!!addressErrorMessage}
        onChange={handleChange}
        disabled={disabled}
      />
      {status === 'OK' && isSuggestionsVisible && (
        <ElevatedContainer>
          <PostalAddressSuggestionsBox ref={ref}>
            {data?.map((suggestion) => {
              const {
                place_id,
                structured_formatting: { main_text, secondary_text }
              } = suggestion
              return (
                main_text &&
                secondary_text && (
                  <PostalAddressSuggestion
                    key={place_id}
                    onClick={() => selectSuggestion(suggestion)}
                  >
                    {`${main_text}, ${secondary_text}`}
                  </PostalAddressSuggestion>
                )
              )
            })}
          </PostalAddressSuggestionsBox>
        </ElevatedContainer>
      )}
      <input hidden {...register('particulars.address.postalAddressMain')} />
      <input
        hidden
        {...register('particulars.address.postalAddressSecondary')}
      />
      <input hidden {...register('particulars.address.postalAddressZipcode')} />
      <input hidden {...register('particulars.address.postalAddressCity')} />
      <input hidden {...register('particulars.address.postalAddressCountry')} />
      <input
        hidden
        {...register('particulars.address.postalAddressAreaLevel1')}
      />
      <input
        hidden
        {...register('particulars.address.postalAddressAreaLevel2')}
      />
      {addressErrorMessage && (
        <InputErrorLabel>{addressErrorMessage}</InputErrorLabel>
      )}
    </InputLabelBlock>
  )
}
